import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProductService } from 'src/app/shared/services/product.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../state/app-state';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionDetail,
    WatchedItemsFlattenedListResponse,
    GetWatcherByIdResponse,
    BiddingService,
    GetUserProxyBids200ResponseResultInner,
    GetMyWatchedItems200Response,
    GetLotFiltersResponse,
    GetLotFiltersResponseDataCategoriesInner,
    PostBidLotState,
} from '@viewbid/ts-angular-client';
import { BiddingUtils, reconcileHighBidder } from '@/vb-utils/bidding.util';
import { TimerService } from '@/shared/services/timerService';
import {
    catchError,
    debounceTime,
    of,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';
import {
    addToWatchlist,
    removeFromWatchlist,
    watchlistTotalLoaded,
} from '../../state/watchlist-page-state/watchlist-page-state.actions';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import {
    EventListeners,
    SocketService,
} from 'src/app/viewbid/shared/services/socket.service';
import {
    activeFilterCount,
    Auction,
    EMPTY_FILTER,
    FilterValue,
    fmParseNum,
    isRangeInvalid,
    Lot,
    VbSnackbarService,
    WatchlistAction,
} from '@viewbid/ui';
import {
    applyAuctionUpdateToWatchedItems,
    AuctionUpdateResult,
    LotResponseWithProxy,
    WatchedItemWithProxy,
    watchedItemToAuction,
    watchedItemToLot,
} from 'src/app/viewbid/shared/utils/lot-card-mapper.util';
import {
    LOT_GRACE_MS,
    LotCountdownTracker,
    hasTimePassed,
} from 'src/app/viewbid/shared/utils/lot-countdown.util';
import { scrollUpTo } from 'src/app/viewbid/shared/utils/scroll-up-to.util';
import { WatchlistService } from 'src/app/viewbid/shared/services/watchlist.service';
import {
    LotCardBidService,
    nextBidAmountFor,
} from 'src/app/viewbid/shared/services/lot-card-bid.service';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';

interface MappedWatchlistRow {
    lot: Lot;
    auction: Auction;
    auctionLotId: string;
}

@Component({
    selector: 'vb-watchlist-page',
    templateUrl: './watchlist-page.component.html',
    styleUrls: ['./watchlist-page.component.scss'],
})
export class WatchlistPageComponent implements OnInit, OnDestroy {
    public products: LotResponseWithProxy[] = [];
    public minPrice: number = 0;
    public maxPrice: number = this.productService.maxPrice;
    public niceSelectOptions: any = this.productService.filterSelect;
    public pageNo: number = 1;
    public pageSize: number = 10;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    public totalCount: number = 0;
    public paginate: any = {};
    public sortBy: string = 'asc';
    public isLoading: boolean = true;
    public unableToLoad: boolean = false;
    public auctionId: string = '';
    public auction!: AuctionDetail;
    public auctionLotsWatchers: Array<GetWatcherByIdResponse> = [];
    activeTab: 'grid' | 'list' = 'list';
    /** True when the viewport is in the lot card's compact range (≤639px).
     *  Compact is an automatically-triggered view, not a user choice: it
     *  overrides the rendered view and hides the grid/list toggle. */
    compactView: boolean = false;
    currentUserId: string = '';
    watchlist: WatchedItemWithProxy[] = [];
    filteredItems: WatchedItemWithProxy[] = [];
    mappedRows: MappedWatchlistRow[] = [];
    bidLoadingByLot: Record<string, boolean> = {};
    userBidLotIds: Set<string> = new Set();
    /** True while getMyWatchedItems is in flight. Socket updates arriving in
     *  this window are buffered below so the load's snapshot — which can be
     *  older than a live bid — can't clobber them (the "winning shown after a
     *  socket said losing" bug). */
    private loadInFlight: boolean = false;
    /** Socket-pushed field patches per lot collected during an in-flight load,
     *  re-applied over the fresh snapshot when the load completes. */
    private readonly socketPatchesDuringLoad: Map<
        string,
        Partial<WatchedItemWithProxy>
    > = new Map();
    /** Auction-settings updates (keyed by auctionId) collected during an
     *  in-flight load, re-applied over the fresh snapshot when it commits so an
     *  AUCTION_UPDATE arriving mid-load isn't clobbered by the snapshot. */
    private readonly auctionPatchesDuringLoad: Map<string, AuctionDetail> =
        new Map();
    private readonly joinedAuctionIds: Set<string> = new Set();
    confirmDialogOpen: boolean = false;
    pendingBidAmount: number = 0;
    pendingBidBpPct: number = 0;
    private pendingBidRow: MappedWatchlistRow | null = null;
    private pendingBidLocation: 'QUICK_BID' | 'MAIN_INPUT_FIELD' = 'QUICK_BID';

    countdownTracker: LotCountdownTracker;

    /** Shared closing grace window (ms), forwarded to each lot card. */
    readonly graceMs: number = LOT_GRACE_MS;

    filter: string[] = [];
    private searchQuery: string | undefined = undefined;
    watchingAuctionLotsIds: string[] = [];
    proxyBids: GetUserProxyBids200ResponseResultInner[] | undefined;

    // Price + category lot filtering, mirroring the auction lot list page.
    public filterModalOpen: boolean = false;
    public filterDraft: FilterValue = { min: '', max: '', cats: [] };
    public appliedFilter: FilterValue = { min: '', max: '', cats: [] };
    public filterCategories: string[] = [];
    public filterCategoryCounts: Record<string, number> | undefined;
    public filterMatchCount: number | undefined;
    public filterTotalCount: number = 0;
    /** Facet totals for the current price draft (category selection excluded). */
    private priceFilteredTotal: number | undefined;
    private facetsLoaded: boolean = false;
    private readonly filterFacets$ = new Subject<FilterValue>();

    private destroy$ = new Subject<void>();
    public isMobile: boolean = false;
    /** Search bar above the list — the scroll target when the reader picks a page. */
    @ViewChild('searchBar', { read: ElementRef })
    private searchBar?: ElementRef<HTMLElement>;

    constructor(
        private store: Store<AppState>,
        private route: ActivatedRoute,
        public productService: ProductService,
        public translate: TranslateService,
        private snackbarService: VbSnackbarService,
        private utils: BiddingUtils,
        private sanitizer: DomSanitizer,
        private biddingUtils: BiddingUtils,
        private timerService: TimerService,
        private deviceCheckService: DeviceCheckService,
        private biddingService: BiddingService,
        private router: Router,
        private watchlistService: WatchlistService,
        private lotCardBidService: LotCardBidService,
        private socketService: SocketService,
        private serverClock: ServerClockService,
    ) {
        this.countdownTracker = new LotCountdownTracker(this.timerService, () =>
            this.serverClock.now(),
        );
    }

    ngOnInit(): void {
        this.countdownTracker.start();

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });

        // The compact card view is its own automatically-triggered view, keyed
        // to the SAME viewport breakpoint as the card's compact CSS. It never
        // rewrites activeTab — the user's grid/list choice survives the window
        // shrinking and is restored verbatim when it widens again.
        this.deviceCheckService.isCompactCards$
            .pipe(takeUntil(this.destroy$))
            .subscribe((compact: boolean) => {
                this.compactView = compact;
            });

        this.deviceCheckService.isSlow().then((isSlow: boolean) => {
            if (isSlow) {
                this.snackbarService.system(
                    this.translate.instant(
                        'Notifications.slowConnectionDetected',
                    ),
                );
            }
        });

        this.utils.getCurrentUserId().subscribe((currentUserId: string) => {
            this.currentUserId = currentUserId;
            this.rebuildMappedRows();
        });

        this.route.queryParams.subscribe((queryParams: Params) => {
            if (
                queryParams['page'] &&
                this.pageNo !== Number(queryParams['page'])
            ) {
                this.setPage(queryParams['page'] ?? 1);
                this.pageNo = queryParams['page'] ?? 1;
            }
        });

        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => {
                this.handleSocketEvent(data);
            });

        // Reconnect REST backstop: refetch watched items on non-recovered reconnect.
        this.socketService.reconnected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.loadData());

        this.filterFacets$
            .pipe(
                debounceTime(250),
                switchMap((draft: FilterValue) =>
                    this.biddingService
                        .getMyWatchedItemsFilters(
                            undefined,
                            fmParseNum(draft.min) ?? undefined,
                            fmParseNum(draft.max) ?? undefined,
                        )
                        .pipe(
                            catchError(() =>
                                of<GetLotFiltersResponse | null>(null),
                            ),
                        ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((response: GetLotFiltersResponse | null) => {
                this.applyFilterFacets(response);
            });
        this.filterFacets$.next(this.filterDraft);

        this.loadData();
        this.productService.showNotes = false;
    }

    ngOnDestroy(): void {
        this.snackbarService.dismissAll();
        this.countdownTracker.stop();
        if (this.joinedAuctionIds.size) {
            this.socketService.subscribeToResources(
                [...this.joinedAuctionIds].map((resourceId: string) => ({
                    resourceId,
                    listen: false,
                })),
            );
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadData(): void {
        this.isLoading = true;
        // Open the load window: buffer socket updates until the snapshot lands.
        this.loadInFlight = true;
        this.socketPatchesDuringLoad.clear();
        this.auctionPatchesDuringLoad.clear();
        this.biddingService
            .getMyWatchedItems(
                this.pageNo,
                this.pageSize,
                undefined,
                this.appliedFilter.cats.length
                    ? this.appliedFilter.cats.join(',')
                    : undefined,
                fmParseNum(this.appliedFilter.min) ?? undefined,
                fmParseNum(this.appliedFilter.max) ?? undefined,
                true,
                this.searchQuery,
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({
                    data,
                    totalCount,
                    page,
                    pageSize,
                }: GetMyWatchedItems200Response) => {
                    this.isLoading = false;
                    this.shareWatchlistTotal(totalCount);
                    if (
                        data.length < 1 &&
                        this.router.url.includes('watchlist')
                    ) {
                        this.loadInFlight = false;
                        this.socketPatchesDuringLoad.clear();
                        this.auctionPatchesDuringLoad.clear();
                        this.filteredItems = [];
                        this.mappedRows = [];
                        this.countdownTracker.setLots([]);
                        this.paginate = this.productService.getPager(
                            0,
                            0,
                            pageSize,
                        );
                        return;
                    }
                    this.watchlist = data as WatchedItemWithProxy[];
                    // Display whatever the backend returns. Whether closed/sold
                    // lots appear is decided server-side, not filtered here.
                    const items: WatchedItemWithProxy[] = this.watchlist.map(
                        (item: WatchedItemWithProxy) => ({
                            ...item,
                            maximumProxyBid:
                                item.maximumProxyBid ?? item.userMaxBid,
                            images: item.images ?? item.imageURL,
                        }),
                    );
                    this.userBidLotIds = new Set(
                        items
                            .filter(
                                (item: WatchedItemWithProxy) =>
                                    item.userMaxBid != null,
                            )
                            .map(
                                (item: WatchedItemWithProxy) =>
                                    item.auctionLotId,
                            ),
                    );
                    if (this.router.url.includes('watchlist')) {
                        this.totalCount = totalCount ?? 0;
                        this.pageNo = page ?? 0;
                        this.paginate = this.productService.getPager(
                            totalCount ?? 0,
                            Number(+(page ?? 0)),
                            pageSize,
                        );
                    }
                    this.commitLoadedItems(items);
                },
                error: () => {
                    this.loadInFlight = false;
                    this.isLoading = false;
                    this.unableToLoad = true;
                },
            });
    }

    private shareWatchlistTotal(totalCount: number | undefined): void {
        if (
            totalCount === undefined ||
            this.searchQuery ||
            this.appliedFilterCount
        ) {
            return;
        }
        this.store.dispatch(watchlistTotalLoaded({ totalCount }));
    }

    /** The view actually rendered. Compact (≤639px viewport) is automatic and
     *  wins; otherwise the user's grid/list choice rules. */
    get effectiveTab(): 'grid' | 'list' {
        return this.compactView ? 'grid' : this.activeTab;
    }

    handleActiveTab(tab: 'grid' | 'list'): void {
        this.activeTab = tab;
        this.loadData();
    }

    changeFilterSelect(selectedOption: { value: string; text: string }): void {
        this.sortByFilter(selectedOption.value);
    }

    get appliedFilterCount(): number {
        return activeFilterCount(this.appliedFilter);
    }

    openFilterModal(): void {
        this.filterDraft = {
            min: this.appliedFilter.min,
            max: this.appliedFilter.max,
            cats: [...this.appliedFilter.cats],
        };
        this.filterFacets$.next(this.filterDraft);
        this.filterModalOpen = true;
    }

    onFilterDraftChange(next: FilterValue): void {
        const priceChanged: boolean =
            next.min !== this.filterDraft.min ||
            next.max !== this.filterDraft.max;
        this.filterDraft = next;
        if (priceChanged) {
            // Counts are stale until the facets round-trip lands; the modal
            // CTA falls back to "Apply filter" while undefined.
            this.filterMatchCount = undefined;
            if (!isRangeInvalid(next)) {
                this.filterFacets$.next(next);
            }
            return;
        }
        this.recomputeFilterMatchCount();
    }

    onFilterApply(): void {
        this.appliedFilter = {
            min: this.filterDraft.min,
            max: this.filterDraft.max,
            cats: [...this.filterDraft.cats],
        };
        this.filterModalOpen = false;
        this.setPage(1);
    }

    onFilterClosed(): void {
        this.filterModalOpen = false;
    }

    clearFilters(): void {
        this.appliedFilter = { ...EMPTY_FILTER, cats: [] };
        this.setPage(1);
    }

    /** Update modal facets from the watched-items filters endpoint. `null`
     *  (fetch error) hides counts so the modal degrades to a plain "Apply
     *  filter" CTA. */
    private applyFilterFacets(response: GetLotFiltersResponse | null): void {
        if (!response?.data) {
            this.filterCategoryCounts = undefined;
            this.priceFilteredTotal = undefined;
            this.filterMatchCount = undefined;
            return;
        }
        const facets: GetLotFiltersResponseDataCategoriesInner[] =
            response.data.categories ?? [];
        if (!this.facetsLoaded) {
            // The chip set and total come from the first (unbounded) fetch;
            // later fetches only narrow the counts by the price draft.
            this.filterCategories = facets
                .map(
                    (facet: GetLotFiltersResponseDataCategoriesInner) =>
                        facet.category,
                )
                .filter((category: string | undefined): category is string =>
                    Boolean(category),
                );
            this.filterTotalCount = response.data.totalCount ?? 0;
            this.facetsLoaded = true;
        }
        const counts: Record<string, number> = {};
        for (const category of this.filterCategories) {
            counts[category] = 0;
        }
        for (const facet of facets) {
            if (facet.category) {
                counts[facet.category] = facet.count ?? 0;
            }
        }
        this.filterCategoryCounts = counts;
        this.priceFilteredTotal = response.data.totalCount;
        this.recomputeFilterMatchCount();
    }

    private recomputeFilterMatchCount(): void {
        if (
            isRangeInvalid(this.filterDraft) ||
            this.priceFilteredTotal === undefined
        ) {
            this.filterMatchCount = undefined;
            return;
        }
        if (!this.filterDraft.cats.length) {
            this.filterMatchCount = this.priceFilteredTotal;
            return;
        }
        const counts: Record<string, number> = this.filterCategoryCounts ?? {};
        this.filterMatchCount = this.filterDraft.cats.reduce(
            (sum: number, category: string) => sum + (counts[category] ?? 0),
            0,
        );
    }

    /** A page picked in either paginator: back up to the search bar, then load. */
    onPageChange(page: number): void {
        scrollUpTo(this.searchBar?.nativeElement, !this.isMobile);
        this.setPage(page);
    }

    setPage(page: number): void {
        this.pageNo = page;
        this.loadData();
    }

    setPageSize(pageSize: number): void {
        this.pageSize = pageSize;
        this.pageNo = 1;
        this.loadData();
    }

    onSearch(query: string): void {
        this.searchQuery = query || undefined;
        this.pageNo = 1;
        this.loadData();
    }

    sortByFilter(_value: string): void {}

    updateFilter(_tags: unknown): void {}

    onWatchlistUpdated(action: WatchlistAction): void {
        const row: WatchedItemWithProxy | undefined = this.filteredItems.find(
            (item: WatchedItemWithProxy) =>
                item.auctionLotId === action.auctionLotsId,
        );
        const title: string | undefined = row?.title;
        if (action.action === 'add') {
            if (!row) return;
            this.watchlistService
                .addToWatchlist(row.auctionId, action.auctionLotsId, title)
                .subscribe({
                    next: (watchlistId: string) => {
                        this.store.dispatch(
                            addToWatchlist({
                                watchlistId: action.auctionLotsId,
                            }),
                        );
                        this.isLoading = true;
                        this.loadData();
                    },
                });
            return;
        }
        if (action.action === 'remove' && action.watcherId) {
            this.watchlistService
                .removeFromWatchlist(action.watcherId, title)
                .then(() => {
                    this.store.dispatch(
                        removeFromWatchlist({
                            watchlistId: action.auctionLotsId,
                        }),
                    );
                    this.utils.removeFromWatchlist(action.auctionLotsId);
                    this.isLoading = true;
                    this.loadData();
                });
        }
    }

    onQuickBidPlaced(row: MappedWatchlistRow): void {
        if (!this.currentUserId || !row.lot.auctionLotsId || !row.lot.auctionId)
            return;
        this.startBid(row, nextBidAmountFor(row.lot), 'QUICK_BID');
    }

    onCustomBidPlaced(row: MappedWatchlistRow, amount: number): void {
        if (!this.currentUserId || !row.lot.auctionLotsId || !row.lot.auctionId)
            return;
        this.startBid(row, amount, 'MAIN_INPUT_FIELD');
    }

    /** Mirrors the lot-details flow: stage the bid, then show the library
     *  confirm dialog unless the user opted out for the lot's auction. */
    private startBid(
        row: MappedWatchlistRow,
        amount: number,
        location: 'QUICK_BID' | 'MAIN_INPUT_FIELD',
    ): void {
        if (!amount) return;
        this.pendingBidRow = row;
        this.pendingBidAmount = amount;
        this.pendingBidBpPct = row.auction.buyerPremium ?? 0;
        this.pendingBidLocation = location;
        if (this.utils.shouldShowBidConfirmation(row.lot.auctionId!)) {
            this.confirmDialogOpen = true;
        } else {
            this.placePendingBid();
        }
    }

    onBidConfirmed(dontAsk: boolean): void {
        if (dontAsk && this.pendingBidRow?.lot.auctionId) {
            this.utils.setSkipBidConfirmation(this.pendingBidRow.lot.auctionId);
        }
        this.confirmDialogOpen = false;
        this.placePendingBid();
    }

    onBidCancelled(): void {
        this.confirmDialogOpen = false;
        this.pendingBidRow = null;
        this.pendingBidAmount = 0;
    }

    onBidRounded(): void {
        this.snackbarService.system(
            this.translate.instant('Bidding.bidRoundedDown'),
        );
    }

    private placePendingBid(): void {
        const row: MappedWatchlistRow | null = this.pendingBidRow;
        const amount: number = this.pendingBidAmount;
        this.pendingBidRow = null;
        if (
            !this.currentUserId ||
            !row?.lot.auctionLotsId ||
            !row.lot.auctionId
        ) {
            return;
        }
        // The clock hit zero (and the card entered its grace window) while the
        // confirm dialog was open — don't land a bid after the lot has ended.
        if (this.isLotBiddingEnded(row.lot)) {
            return;
        }
        this.setBidLoading(row.lot.auctionLotsId, true);
        this.lotCardBidService.placeConfirmedBid(
            {
                auctionId: row.lot.auctionId,
                auctionLotsId: row.lot.auctionLotsId,
                lotId: row.lot.lotId ?? row.lot.id,
                lotTitle: row.lot.title,
                lotNumber: row.lot.number,
                buyerPremium: row.auction.buyerPremium ?? 0,
                amount,
                minimumBidAmount: nextBidAmountFor(row.lot),
                pageName:
                    this.effectiveTab === 'list'
                        ? 'WATCHLIST_LIST'
                        : 'WATCHLIST_GRID',
                pageLocation: this.pendingBidLocation,
            },
            this.currentUserId,
            (placed: boolean, lotState?: PostBidLotState) => {
                this.setBidLoading(row.lot.auctionLotsId!, false);
                if (placed) {
                    this.userBidLotIds.add(row.lot.auctionLotsId!);
                    if (lotState) {
                        this.applyLotState(
                            row.lot.auctionLotsId!,
                            lotState,
                            amount,
                        );
                    } else {
                        this.applyOptimisticHighBidder(
                            row.lot.auctionLotsId!,
                            amount,
                        );
                    }
                }
            },
        );
    }

    userHasBidOn(lot: Lot): boolean {
        if (!lot.auctionLotsId) return false;
        return (
            this.userBidLotIds.has(lot.auctionLotsId) ||
            (typeof lot.maximumProxyBid === 'number' && lot.maximumProxyBid > 0)
        );
    }

    isLotBiddingEnded(lot: Lot): boolean {
        if (!lot.auctionLotsId) return false;
        const item: WatchedItemWithProxy | undefined = this.filteredItems.find(
            (i: WatchedItemWithProxy) => i.auctionLotId === lot.auctionLotsId,
        );
        return hasTimePassed(item?.lotToDatetime, this.serverClock.now());
    }

    handleLotHidden(): void {
        this.loadData();
    }

    handleLoadingComplete(): void {}

    handleNotesCanvas(): void {
        this.productService.showNotes = !this.productService.showNotes;
    }

    sanitizeHtml(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    trackByRow(_index: number, row: MappedWatchlistRow): string {
        return row.auctionLotId;
    }

    private setBidLoading(auctionLotsId: string, loading: boolean): void {
        this.bidLoadingByLot = {
            ...this.bidLoadingByLot,
            [auctionLotsId]: loading,
        };
    }

    private applyLotState(
        auctionLotId: string,
        lotState: PostBidLotState,
        bidAmount: number,
    ): void {
        const idx: number = this.filteredItems.findIndex(
            (item: WatchedItemWithProxy) => item.auctionLotId === auctionLotId,
        );
        if (idx === -1) return;
        const existing: WatchedItemWithProxy = this.filteredItems[idx];
        const newMax: number = Math.max(
            existing.maximumProxyBid ?? 0,
            bidAmount,
        );
        this.filteredItems[idx] = {
            ...existing,
            currentBid: lotState.currentBid,
            minimumBidAmount: lotState.minimumNextBid,
            lotToDatetime: lotState.toDatetime,
            currentHighBidderId: reconcileHighBidder(
                existing.currentHighBidderId,
                this.currentUserId,
                lotState.isHighBidder,
            ),
            maximumProxyBid: newMax,
            userMaxBid: newMax,
        };
        this.rebuildMappedRows();
    }

    private applyOptimisticHighBidder(
        auctionLotId: string,
        bidAmount: number,
    ): void {
        const idx: number = this.filteredItems.findIndex(
            (item: WatchedItemWithProxy) => item.auctionLotId === auctionLotId,
        );
        if (idx === -1) return;
        const existing: WatchedItemWithProxy = this.filteredItems[idx];
        // Our standing max rose either way — surface it now so the pill's
        // "your max" is correct immediately, regardless of who leads.
        const newMax: number = Math.max(
            existing.maximumProxyBid ?? 0,
            bidAmount,
        );
        // When proxy bidding is on and another user already holds the high bid,
        // their hidden proxy max may instantly out-counter ours. We can't know
        // that max locally, so claiming the high bid here flashes "winning"
        // until the proxy's auto-counter arrives (via PAGE_LOAD) and flips it to
        // "losing" — the bug. Leave the high bidder as-is and let the socket
        // resolve it. Every other case (no leader yet, or we already lead) is a
        // certain win, so claim it to avoid a spurious "losing" flash.
        const otherUserLeads: boolean =
            !!existing.currentHighBidderId &&
            existing.currentHighBidderId !== this.currentUserId;
        if (existing.isProxyBiddingEnabled && otherUserLeads) {
            this.filteredItems[idx] = {
                ...existing,
                maximumProxyBid: newMax,
                userMaxBid: newMax,
            };
            this.rebuildMappedRows();
            return;
        }
        this.filteredItems[idx] = {
            ...existing,
            currentHighBidderId: this.currentUserId,
            maximumProxyBid: newMax,
            userMaxBid: newMax,
        };
        this.rebuildMappedRows();
    }

    /**
     * Applies a socket-pushed field patch to the matching watched item and,
     * while a load is in flight, buffers it so the load's snapshot can't
     * overwrite it when it lands. Returns the item's index (or -1 if not
     * currently shown — still buffered, since the load may be about to add it).
     */
    private applySocketPatch(
        auctionLotId: string | undefined,
        patch: Partial<WatchedItemWithProxy>,
    ): number {
        if (!auctionLotId) return -1;
        if (this.loadInFlight) {
            this.socketPatchesDuringLoad.set(auctionLotId, {
                ...this.socketPatchesDuringLoad.get(auctionLotId),
                ...patch,
            });
        }
        const idx: number = this.filteredItems.findIndex(
            (item: WatchedItemWithProxy) => item.auctionLotId === auctionLotId,
        );
        if (idx !== -1) {
            this.filteredItems[idx] = { ...this.filteredItems[idx], ...patch };
        }
        return idx;
    }

    /**
     * Commits a freshly-loaded snapshot, overlaying any socket patches that
     * arrived while the load was in flight so live state (e.g. a bid that
     * landed mid-load) survives the snapshot. Ends the load window.
     */
    private commitLoadedItems(items: WatchedItemWithProxy[]): void {
        let next: WatchedItemWithProxy[] = items.map(
            (item: WatchedItemWithProxy) => {
                const patch: Partial<WatchedItemWithProxy> | undefined =
                    item.auctionLotId
                        ? this.socketPatchesDuringLoad.get(item.auctionLotId)
                        : undefined;
                return patch ? { ...item, ...patch } : item;
            },
        );
        for (const detail of this.auctionPatchesDuringLoad.values()) {
            next = applyAuctionUpdateToWatchedItems(next, detail).items;
        }
        this.filteredItems = next;
        this.loadInFlight = false;
        this.socketPatchesDuringLoad.clear();
        this.auctionPatchesDuringLoad.clear();
        this.rebuildMappedRows();
        this.subscribeResourceSockets();
    }

    private handleSocketEvent(data: string): void {
        let event: any;
        try {
            event = JSON.parse(data);
        } catch {
            return;
        }

        if (event.action === 'AUCTION_UPDATE' && event.data?.id) {
            if (this.loadInFlight) {
                this.auctionPatchesDuringLoad.set(event.data.id, event.data);
            }
            const merged: AuctionUpdateResult =
                applyAuctionUpdateToWatchedItems(
                    this.filteredItems,
                    event.data,
                );
            if (merged.changed) {
                this.filteredItems = merged.items;
                this.rebuildMappedRows();
            }
            return;
        }

        if (event.action === 'UPDATE_TIMES_EVENT') {
            // Anti-snipe fan-out: every open lot in the sniped lot's group
            // (or the whole auction when live) gets a new close time. Patch
            // the rows so rebuildMappedRows() reseeds the tracker from the
            // extended clock instead of snapping back to the old one.
            const lots: { auctionLotId?: string; endDate?: string }[] =
                Array.isArray(event.data?.lots) ? event.data.lots : [];
            let touched: boolean = false;
            for (const lot of lots) {
                if (!lot.auctionLotId || !lot.endDate) continue;
                const idx: number = this.applySocketPatch(lot.auctionLotId, {
                    lotToDatetime: lot.endDate,
                });
                if (idx !== -1) touched = true;
            }
            if (touched) this.rebuildMappedRows();
            return;
        }

        if (event.action === 'PAGE_LOAD') {
            const idx: number = this.applySocketPatch(
                event.data.auctionLotsId,
                {
                    lotToDatetime: event.data.endDate,
                    minimumBidAmount: event.data.minimumBid,
                    currentHighBidderId: event.data.highBidder ?? '',
                    reserveAmount: event.data.reserveAmount,
                    currentBid: event.data.currentPrice,
                },
            );
            if (idx !== -1) this.rebuildMappedRows();
            return;
        }

        if (event.action === 'USER_MAX_BID_UPDATE') {
            const idx: number = this.applySocketPatch(event.data.auctionLotId, {
                maximumProxyBid: event.data.userMaxBid,
                userMaxBid: event.data.userMaxBid,
            });
            if (event.data.userMaxBid === 0 && event.data.auctionLotId) {
                this.userBidLotIds.delete(event.data.auctionLotId);
            }
            if (idx !== -1) this.rebuildMappedRows();
            return;
        }

        if (
            event.action === 'NEW_WATCHER_FROM_BID' &&
            event.data.accountMappingsId === this.currentUserId
        ) {
            const idx: number = this.applySocketPatch(event.data.auctionLotId, {
                watcherId: event.data.id,
            });
            this.biddingUtils.updateWatchlist(event.data, event.data.id);
            if (idx !== -1) this.rebuildMappedRows();
        }
    }

    private subscribeResourceSockets(): void {
        const auctionIds: string[] = this.filteredItems
            .map((item: WatchedItemWithProxy) => item.auctionId)
            .filter((id: string | undefined): id is string => !!id);
        auctionIds.forEach((id: string) => this.joinedAuctionIds.add(id));

        const lotIds: string[] = this.filteredItems
            .map((item: WatchedItemWithProxy) => item.auctionLotId)
            .filter((id: string | undefined): id is string => !!id);
        const resources: EventListeners[] = [
            ...new Set([...lotIds, ...auctionIds]),
        ].map((resourceId: string) => ({ resourceId, listen: true }));
        if (resources.length) {
            this.socketService.subscribeToResources(resources);
        }
    }

    private rebuildMappedRows(): void {
        this.mappedRows = this.filteredItems.map(
            (item: WatchedItemWithProxy) => ({
                auctionLotId: item.auctionLotId,
                lot: watchedItemToLot(item),
                auction: watchedItemToAuction(item),
            }),
        );
        this.countdownTracker.setLots(
            this.filteredItems.map((item: WatchedItemWithProxy) => ({
                auctionLotId: item.auctionLotId,
                fromDatetime: item.fromDatetime,
                toDatetime: item.lotToDatetime,
            })),
        );
    }
}
